import { __ } from "services/Translator";

export const URGENT_THRESHOLD_MS = 60 * 60 * 1000;

// Example prize economics — shown only in the rules section, badged as an
// example when the race payload omits its own prize config (isPrizeTableFallback).
// Leaderboard rows never use these values (see getPrizeForRank). Built lazily:
// __() must run after the /translations payload loads, not at module import.
export const getExamplePrizeTable = () => [
  { label: __("1st"), fs: 200, first: true },
  { label: __("2nd"), fs: 100 },
  { label: __("3rd"), fs: 70 },
  { label: __("4th"), fs: 50 },
  { label: __("5th–6th"), fs: 20 },
  { label: __("7th–10th"), fs: 10 },
];

// True when we had to fall back to the hard-coded PRIZE_TABLE because the race
// payload supplied no prize config — callers should badge the table as an example.
export function isPrizeTableFallback(race) {
  const configured = race?.prize_table || race?.prizes;
  return !(Array.isArray(configured) && configured.length);
}

// Prefer the prize tiers supplied by the race API payload; fall back to the
// hard-coded example table when the API can't supply them yet.
export function getPrizeTable(race) {
  const exampleTable = getExamplePrizeTable();
  const configured = race?.prize_table || race?.prizes;
  if (Array.isArray(configured) && configured.length) {
    return configured.map((tier, i) => ({
      label: tier.label || tier.rank || exampleTable[i]?.label || `${i + 1}`,
      fs: Number(tier.fs ?? tier.free_spins ?? 0),
      first: tier.first ?? i === 0,
    }));
  }
  return exampleTable;
}

// Prize shown per leaderboard row. Only ever shows a number the platform is
// actually committed to: the row's own payout or the race's configured prize
// table. NEVER falls back to the hard-coded example values — a fabricated
// prize on a real-money race is a misrepresented commitment.
export function getPrizeForRank(rank, row, race) {
  if (row?.free_spins) {
    return `${row.free_spins} ${__("FS")}`;
  }
  if (race && !isPrizeTableFallback(race)) {
    const fs = getPrizeTable(race)[rank - 1]?.fs;
    if (fs) {
      return `${fs} ${__("FS")}`;
    }
  }
  return "—";
}

// Display name for the AUTHENTICATED leaderboard. Never falls back to
// player_login — a raw account username is PII, not a public competition
// name — so an entry with only a login shows a neutral "Player" instead.
export function getLeaderboardDisplayName(entry) {
  if (!entry) return "";
  return entry.player_name || entry.player_nickname || __("Player");
}

// Display name for the LOGGED-OUT (public) teaser board. Masks any available
// public name to a single initial and NEVER reads player_login, so no real
// identity leaks to a stranger.
export function getMaskedDisplayName(entry) {
  if (!entry) return __("Player");
  const raw = entry.player_name || entry.player_nickname || "";
  const first = String(raw).trim().charAt(0);
  return first ? `${first.toUpperCase()}••••` : __("Player");
}

export function getInitials(name) {
  if (!name) return "?";
  const parts = String(name).replace(/[^a-zA-Z0-9\s]/g, "").trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return String(name).slice(0, 2).toUpperCase();
}

export function formatPoints(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function getRankDelta(row) {
  const change =
    row?.rank_change ??
    row?.rank_delta ??
    (row?.previous_rank != null && row?.rank != null
      ? row.previous_rank - row.rank
      : null);
  if (change == null || change === 0) {
    return { type: "same", value: 0 };
  }
  if (change > 0) {
    return { type: "up", value: change };
  }
  return { type: "down", value: Math.abs(change) };
}

export function getRowClassName(rank, isMe) {
  let cls = "lb-row";
  if (rank === 1) cls += " top1";
  else if (rank === 2) cls += " top2";
  else if (rank === 3) cls += " top3";
  if (isMe) cls += " me";
  return cls;
}

export function getMedalClass(rank) {
  if (rank === 1) return "medal gold";
  if (rank === 2) return "medal silver";
  if (rank === 3) return "medal bronze";
  return "medal n";
}

export function getRaceEndDate(race) {
  if (!race) return null;
  if (race.end_at) return new Date(race.end_at);
  if (race.expiry_date) {
    const parsed = new Date(race.expiry_date.replace(" ", "T") + "Z");
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  if (race.ending_in != null) {
    return new Date(Date.now() + Number(race.ending_in) * 1000);
  }
  return null;
}

export function isCurrentPlayerRow(row, userDetails) {
  if (!row || !userDetails) return false;
  if (row.is_me || row.is_current_player) return true;
  const login = userDetails.login || userDetails.player_login;
  const id = userDetails.id || userDetails.player_id;
  return (
    (login && row.player_login === login) ||
    (id && (row.player_id === id || row.player_login === String(id)))
  );
}

export function findUserLeaderboardEntry(leaderboard, userDetails) {
  if (!leaderboard?.length || !userDetails) return null;
  const idx = leaderboard.findIndex((row) =>
    isCurrentPlayerRow(row, userDetails)
  );
  if (idx === -1) return null;
  return { row: leaderboard[idx], rank: idx + 1 };
}
